import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'localization';
import { TabView, TabPanel, TabViewTabChangeEvent } from 'primereact/tabview';
import { useImpersonation } from '@center-technologies/shared-api-clients';
import type {
  ChannelItem,
  ChannelItemPermissions,
  ForkPolicy,
} from 'types/channel';
import { useApiClientsOrNull } from '../../../../context/ApiClientsContext';
import { useDialogFocus } from '../../../../hooks/useDialogFocus';
import { useItemDetail } from '../../../../hooks/useItemDetail';
import { useItemAudit } from '../../../../hooks/useItemAudit';
import {
  addForkGrant,
  addItemManager,
  removeForkGrant,
  removeItemManager,
  updateForkPolicy,
} from '../../../../api/marketplaceApi';
import ItemDetailTabMolecule from '../../../molecules/marketplace/ItemDetailTabMolecule/ItemDetailTabMolecule';
import ItemPermissionsTabMolecule from '../../../molecules/marketplace/ItemPermissionsTabMolecule/ItemPermissionsTabMolecule';
import ItemAuditTabMolecule from '../../../molecules/marketplace/ItemAuditTabMolecule/ItemAuditTabMolecule';
import './ItemDetailDialogOrganism.scss';

export interface ItemDetailDialogOrganismProps {
  channelId: string;
  item: ChannelItem | null;
  visible: boolean;
  onHide: () => void;
  permissions: ChannelItemPermissions;
  onItemUpdated: () => void;
}

export interface ItemDetailDirtyState {
  setDirty: (dirty: boolean) => void;
}

const ItemDetailDirtyContext = createContext<ItemDetailDirtyState>({
  setDirty: () => {
    /* default no-op */
  },
});

export function useItemDetailDirty(): ItemDetailDirtyState {
  return useContext(ItemDetailDirtyContext);
}

const ItemDetailDialogOrganism: React.FC<ItemDetailDialogOrganismProps> = ({
  channelId,
  item,
  visible,
  onHide,
  permissions,
  onItemUpdated,
}) => {
  const { t } = useTranslation();
  const apiClients = useApiClientsOrNull();
  const { impersonationDetails } = useImpersonation();
  const organizationId = impersonationDetails?.organizationId;

  const itemId = visible ? item?.id ?? null : null;

  const detail = useItemDetail(channelId, itemId);
  const audit = useItemAudit(channelId, itemId);

  const [activeIndex, setActiveIndex] = useState(0);
  const [isMutating, setIsMutating] = useState(false);
  const dirtyRef = useRef(false);
  const dialogRef = useRef<HTMLDivElement>(null);

  useDialogFocus(visible, dialogRef);

  const dirtyContextValue = useMemo<ItemDetailDirtyState>(
    () => ({
      setDirty: (dirty) => {
        dirtyRef.current = dirty;
      },
    }),
    [],
  );

  useEffect(() => {
    if (!visible) return;
    setActiveIndex(0);
  }, [visible, item?.id]);

  const runMutation = useCallback(
    async (fn: () => Promise<void>) => {
      setIsMutating(true);
      try {
        await fn();
        detail.refetch();
        audit.refetch();
        onItemUpdated();
      } finally {
        setIsMutating(false);
      }
    },
    [detail, audit, onItemUpdated],
  );

  const handleUpdateForkPolicy = useCallback(
    async (nextPolicy: ForkPolicy) => {
      if (!apiClients || !organizationId || !itemId) return;
      await runMutation(() =>
        updateForkPolicy(
          apiClients.schemaClient,
          channelId,
          itemId,
          nextPolicy,
        ),
      );
    },
    [apiClients, organizationId, channelId, itemId, runMutation],
  );

  const handleAddManager = useCallback(
    async (identityId: string) => {
      if (!apiClients || !organizationId || !itemId) return;
      await runMutation(() =>
        addItemManager(
          apiClients.schemaClient,
          channelId,
          itemId,
          identityId,
        ),
      );
    },
    [apiClients, organizationId, channelId, itemId, runMutation],
  );

  const handleRemoveManager = useCallback(
    async (identityId: string) => {
      if (!apiClients || !organizationId || !itemId) return;
      await runMutation(() =>
        removeItemManager(
          apiClients.schemaClient,
          channelId,
          itemId,
          identityId,
        ),
      );
    },
    [apiClients, organizationId, channelId, itemId, runMutation],
  );

  const handleAddForkGrant = useCallback(
    async (identityId: string) => {
      if (!apiClients || !organizationId || !itemId) return;
      await runMutation(() =>
        addForkGrant(
          apiClients.schemaClient,
          channelId,
          itemId,
          identityId,
        ),
      );
    },
    [apiClients, organizationId, channelId, itemId, runMutation],
  );

  const handleRemoveForkGrant = useCallback(
    async (identityId: string) => {
      if (!apiClients || !organizationId || !itemId) return;
      await runMutation(() =>
        removeForkGrant(
          apiClients.schemaClient,
          channelId,
          itemId,
          identityId,
        ),
      );
    },
    [apiClients, organizationId, channelId, itemId, runMutation],
  );

  const attemptClose = useCallback(() => {
    if (isMutating) return;
    if (dirtyRef.current) {
      // eslint-disable-next-line no-alert
      const confirmed = globalThis.confirm(
        t('marketplace.itemDetail.unsavedChanges.message'),
      );
      if (!confirmed) return;
      dirtyRef.current = false;
    }
    onHide();
  }, [isMutating, onHide, t]);

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        attemptClose();
      }
    },
    [attemptClose],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        attemptClose();
      }
    },
    [attemptClose],
  );

  const showManagementTabs = permissions.canManage;

  const tabs = useMemo<Array<'details' | 'permissions' | 'audit'>>(() => {
    const list: Array<'details' | 'permissions' | 'audit'> = ['details'];
    if (showManagementTabs) {
      list.push('permissions', 'audit');
    }
    return list;
  }, [showManagementTabs]);

  const handleTabChange = useCallback((e: TabViewTabChangeEvent) => {
    setActiveIndex(e.index);
  }, []);

  if (!visible) return null;

  const displayItem = detail.item ?? item;
  const hasItem = !!displayItem;
  const activeTab = tabs[activeIndex] ?? 'details';

  return (
    <div
      className="marketplace-dialog-overlay"
      onClick={handleOverlayClick}
      onKeyDown={handleKeyDown}
      role="presentation"
    >
      <div
        ref={dialogRef}
        className="marketplace-dialog marketplace-item-detail-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="item-detail-dialog-title"
      >
        <div className="marketplace-dialog__header">
          <h2
            id="item-detail-dialog-title"
            className="marketplace-dialog__title"
          >
            {t('marketplace.itemDetail.title')}
          </h2>
          <button
            type="button"
            className="marketplace-dialog__close-btn"
            onClick={attemptClose}
            disabled={isMutating}
            aria-label={t('marketplace.itemDetail.close')}
            data-testid="item-detail-close-btn"
          >
            &times;
          </button>
        </div>

        <div className="marketplace-item-detail-dialog__tabs">
          <TabView
            activeIndex={activeIndex}
            onTabChange={handleTabChange}
            scrollable
          >
            {tabs.map((tab) => (
              <TabPanel
                key={tab}
                header={t(`marketplace.itemDetail.tabs.${tab}`)}
              />
            ))}
          </TabView>
        </div>

        <div
          className="marketplace-dialog__body marketplace-item-detail-dialog__body"
          data-testid="item-detail-dialog-body"
        >
          {detail.loading && !hasItem && (
            <div
              className="marketplace-item-detail-dialog__loading"
              data-testid="item-detail-loading"
              role="status"
            >
              <i className="pi pi-spin pi-spinner" aria-hidden="true" />
              <span>{t('marketplace.itemDetail.loading')}</span>
            </div>
          )}

          {detail.error && !hasItem && (
            <div
              className="marketplace-item-detail-dialog__error"
              data-testid="item-detail-error"
              role="alert"
            >
              {t('marketplace.itemDetail.loadError')}
            </div>
          )}

          <ItemDetailDirtyContext.Provider value={dirtyContextValue}>
            {displayItem && activeTab === 'details' && (
              <ItemDetailTabMolecule
                item={displayItem}
                syndicationInfo={detail.syndicationInfo}
                canEdit={permissions.canManage}
              />
            )}

            {displayItem && activeTab === 'permissions' && showManagementTabs && (
              <ItemPermissionsTabMolecule
                forkPolicy={detail.permissions?.forkPolicy ?? 'anyone'}
                managers={detail.permissions?.managers ?? []}
                forkGrants={detail.permissions?.forkGrants ?? []}
                isMutating={isMutating}
                loading={detail.loading && !detail.permissions}
                error={detail.error ? new Error(detail.error) : null}
                canManage={permissions.canManage}
                onUpdateForkPolicy={handleUpdateForkPolicy}
                onAddManager={handleAddManager}
                onRemoveManager={handleRemoveManager}
                onAddForkGrant={handleAddForkGrant}
                onRemoveForkGrant={handleRemoveForkGrant}
              />
            )}

            {displayItem && activeTab === 'audit' && showManagementTabs && (
              <ItemAuditTabMolecule
                entries={audit.entries}
                hasMore={audit.hasMore}
                loading={audit.loading}
                error={audit.error}
                onLoadMore={audit.loadMore}
              />
            )}
          </ItemDetailDirtyContext.Provider>
        </div>
      </div>
    </div>
  );
};

ItemDetailDialogOrganism.displayName = 'ItemDetailDialogOrganism';

export default ItemDetailDialogOrganism;
